import { Download, Power } from '@orgus/ui/icons';
import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { clip } from '@orgus/protocol';
import { Button, ConfirmDialog, Dialog, ErrorState, IconButton, TerminalPane, Textarea, cn, formatCompact, shortPath, toast } from '@orgus/ui';
import { useMachine, useSession } from '../../lib/store.ts';
import { modelLabel } from '../sessions/labels.ts';
import { closeTerminal, controlTerminal, recordingUrl } from './api.ts';
import { agentLabel, lockFor, paneState } from './model.ts';
import { useTerminal } from './useTerminal.ts';

export interface TerminalViewProps {
  termId: string;
  compact?: boolean;
  /** Extra header actions from the page (remove from the split view). */
  actions?: ReactNode;
  /** The pane title's heading level (TerminalPane): 2 on the Terminals page. */
  headingLevel?: 2 | 3;
  className?: string;
  style?: CSSProperties;
}

/** A person's name as it starts a sentence. */
const capital = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** One live terminal with its controls: take control (also from someone typing), hand back (with a note to Claude), end. */
export function TerminalView({ termId, compact, actions, headingLevel, className, style }: TerminalViewProps) {
  const conn = useTerminal(termId);
  const term = conn.term;
  const machine = useMachine(term?.machineId);
  const session = useSession(term?.sessionId ?? null);
  const [busy, setBusy] = useState(false);
  const [actError, setActError] = useState<unknown>(null);
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState('');
  const [forceOpen, setForceOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);
  const noteRef = useRef<HTMLTextAreaElement>(null);

  const you = conn.you?.actor ?? null;
  const lock = lockFor(term, you);
  const state = paneState(term, conn.phase);
  const writable = !!lock?.you && state === 'live';
  const isSession = term?.kind === 'session';
  // A sign-in terminal (ADR 0071) holds nothing to lose: stopping it needs no confirmation.
  const isSignIn = term?.kind === 'sign-in';

  const act = async (fn: () => Promise<unknown>, done?: string) => {
    setBusy(true);
    setActError(null);
    try {
      await fn();
      if (done) toast.success(done);
    } catch (e) {
      setActError(e);
    } finally {
      setBusy(false);
    }
  };

  const take = () => act(() => controlTerminal(termId, { action: 'take', interrupt: true }));
  // A sign-in terminal waits for the code from the browser (ADR 0088): the person can type at once,
  // without finding Take control first, unless someone else already holds it. Taken once.
  const tookSignIn = useRef(false);
  useEffect(() => {
    if (!isSignIn || state !== 'live' || term?.lock || !you || tookSignIn.current) return;
    tookSignIn.current = true;
    void take();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignIn, state, term?.lock, you]);
  const release = (withNote: string) =>
    act(
      async () => {
        await controlTerminal(termId, {
          action: 'release',
          ...(withNote.trim() ? { note: withNote.trim() } : {}),
        });
        setNote('');
        setNoteOpen(false);
      },
      withNote.trim() ? 'Handed back with your note' : 'Handed back',
    );

  const status = {
    agent: term ? agentLabel(term) : 'Terminal',
    model: isSession ? modelLabel(term?.model ?? session?.model) : null,
    machine: machine?.name,
    startedAt: term?.startedAt,
    endedAt: term?.endedAt,
    tokens: session ? `${formatCompact(session.tokens.input)} in / ${formatCompact(session.tokens.output)} out` : undefined,
    viewers: term?.viewers,
    backend: term?.backend,
  };

  const title = term ? (
    <span className="flex min-w-0 items-baseline gap-2">
      <span className="truncate">{term.title}</span>
      {!compact && <span className="hidden truncate font-mono text-caption font-normal text-fg-subtle lg:inline">{shortPath(term.cwd, 40)}</span>}
    </span>
  ) : (
    'Terminal'
  );

  // An action that failed (take control, hand back, end) or a refusal on the socket, said above the pane.
  const problem = actError ?? conn.notice;
  const dismiss = () => {
    setActError(null);
    conn.clearNotice();
  };

  return (
    <>
      <div className={cn('flex min-h-0 flex-col gap-2', className)} style={style}>
        {problem != null && (
          <ErrorState
            variant="inline"
            error={problem}
            action={
              <Button variant="ghost" size="sm" onClick={dismiss}>
                Dismiss
              </Button>
            }
          />
        )}
        <TerminalPane
          className="min-h-0 flex-1"
          compact={compact}
          windowsPty={machine?.os === 'windows'}
          headingLevel={headingLevel}
          label={`Terminal: ${term?.title ?? termId}`}
          title={title}
          source={conn.source}
          state={state}
          error={conn.error ?? undefined}
          onRetry={conn.retry}
          cols={term?.cols ?? 100}
          rows={term?.rows ?? 26}
          writable={writable}
          lock={lock}
          status={status}
          exitCode={term?.exitCode ?? conn.exitCode}
          busy={busy}
          onTakeControl={conn.you ? take : undefined}
          onHandBack={() => (isSession ? setNoteOpen(true) : void release(''))}
          onForce={conn.you?.canForce ? () => setForceOpen(true) : undefined}
          actions={
            <>
              {/* On a phone the title needs the room; the recording download stays on wider screens. */}
              {term && conn.phase !== 'error' && (
                <IconButton label="Download the recording" icon={<Download />} size="sm" className="hidden sm:inline-flex" onClick={() => window.open(recordingUrl(termId), '_blank', 'noopener')} />
              )}
              {term && term.state !== 'exited' && (
                <IconButton label={isSession ? 'End this session' : isSignIn ? 'Stop signing in' : 'Close this shell'} icon={<Power />} size="sm" variant="danger" onClick={() => (isSignIn ? void act(() => closeTerminal(termId)) : setEndOpen(true))} />
              )}
              {actions}
            </>
          }
        />
      </div>

      <Dialog
        open={noteOpen}
        onOpenChange={setNoteOpen}
        title="Hand back to Claude"
        initialFocus={() => noteRef.current}
        description="Tell Claude what you changed so it can pick up from there."
        footer={
          <>
            <Button variant="ghost" onClick={() => void release('')} disabled={busy}>
              Hand back without a note
            </Button>
            <Button variant="primary" loading={busy} onClick={() => void release(note)} disabled={!note.trim()}>
              Hand back with note
            </Button>
          </>
        }
      >
        <Textarea
          label="What you changed"
          placeholder="I fixed the import in SessionRow.tsx and ran the tests. Carry on with the drawer."
          value={note}
          onChange={(e) => setNote(e.target.value)}
          ref={noteRef}
          rows={4}
          hint="Sent to the session as a message from you."
        />
      </Dialog>

      {term?.lock && (
        <ConfirmDialog
          open={forceOpen}
          onOpenChange={setForceOpen}
          title={`Take control from ${capital(term.lock.label)}?`}
          description={`${capital(term.lock.label)} watches instead, and the audit log records it.`}
          confirmLabel="Take control"
          tone="primary"
          onConfirm={() =>
            act(() =>
              controlTerminal(termId, {
                action: 'force',
                interrupt: isSession,
              }),
            )
          }
        />
      )}

      {term && (
        <ConfirmDialog
          open={endOpen}
          onOpenChange={setEndOpen}
          title={isSession ? `End "${clip(term.title.replace(/^Claude Code: /, ''), 48)}"?` : `Close ${term.title}?`}
          description={
            isSession
              ? `This stops Claude Code on ${machine?.name ?? 'its machine'} and everything it started. The transcript and the recording stay.`
              : `This ends the shell on ${machine?.name ?? 'its machine'} and anything still running in it. The recording stays.`
          }
          confirmLabel={isSession ? 'End session' : 'Close shell'}
          onConfirm={() => act(() => closeTerminal(termId), isSession ? 'Session ended' : 'Shell closed')}
        />
      )}
    </>
  );
}
